import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { IntegrationTypeIcon } from '../components/IntegrationTypeIcon/IntegrationTypeIcon';
import { ServiceRow, ServiceRowAction } from './servicesPanelRows';
import { MARK_ICON_SIZE } from './servicesPanelServices';
import { servicesPanelMessages } from './servicesPanelMessages';
import { ACTION_LABELS, lineText } from './serviceRowTexts';
import { withBrandWord } from '../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for ServicesPanel; each part uses its own slice of it */
import styles from './ServicesPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ServiceRowViewProps {
  row: ServiceRow;
  /** The PATCH of this row runs (design d3 "Saving"). */
  isSaving: boolean;
  /** The row got its link a moment ago (design d3 "Linked just now"). */
  isHighlighted: boolean;
  onAction: (row: ServiceRow, action: ServiceRowAction) => void;
  /**
   * The extras box under the link (design service-extras a1), for example "Spotify extras". It
   * spans the name and action columns, and the full row below 700 px.
   */
  extras?: React.ReactNode;
}

/** One service of "Where it plays" (design d3): the mark, the name, the link, the actions. */
const ServiceRowViewComponent: React.FC<ServiceRowViewProps> = ({ row, isSaving, isHighlighted, onAction, extras }) => {
  const intl = useIntl();
  const sub = isSaving ? intl.formatMessage(servicesPanelMessages.saving) : lineText(intl, row);

  return (
    <li
      className={clsx(styles.row, isHighlighted && styles.rowHighlight, extras !== undefined && styles.rowHost)}
      data-testid={`service-row-${row.integrationType}`}
    >
      <span className={styles.mark}>
        <IntegrationTypeIcon integrationType={row.integrationType} size={MARK_ICON_SIZE} square />
      </span>
      <div className={styles.text}>
        <span className={styles.name}>
          {row.name}
          {isHighlighted && (
            <span className={clsx(styles.chip, styles.chipOk)}>
              {intl.formatMessage(servicesPanelMessages.chipLinked)}
            </span>
          )}
        </span>
        <small className={clsx(styles.sub, (row.line === 'notLinked' || isSaving) && styles.subPlain)}>
          {withBrandWord(sub)}
        </small>
      </div>
      <div className={styles.actions}>
        {row.actions.map((action) => {
          const label = intl.formatMessage(ACTION_LABELS[action]);
          if (action === 'open' && row.url !== null) {
            return (
              <a key={action} className={styles.link} href={row.url} target="_blank" rel="noopener noreferrer">
                {label}
              </a>
            );
          }
          return (
            <button
              key={action}
              type="button"
              disabled={isSaving}
              className={styles.link}
              onClick={() => onAction(row, action)}
            >
              {label}
            </button>
          );
        })}
      </div>
      {extras !== undefined && <div className={styles.extras}>{extras}</div>}
    </li>
  );
};

export const ServiceRowView = TypedMemo(ServiceRowViewComponent);
